/**
 * Osservabilità applicativa.
 *
 * Emette log strutturati con un correlation ID stabile per sessione, così una
 * segnalazione dell'utente è ricollegabile agli eventi lato server e alle righe
 * di audit. È anche il punto in cui innestare in futuro un esportatore
 * OpenTelemetry (e quindi Dynatrace) senza toccare il resto del codice.
 *
 * REGOLA: nessun log contiene password, token, chiavi o dati di pagamento.
 * `redact()` rimuove i campi sensibili prima della serializzazione.
 */

import { env, isProduction } from '@/config/env';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogContext {
  readonly [key: string]: unknown;
}

/** Identifica la sessione del browser attraverso tutte le richieste. */
const correlationId = crypto.randomUUID();

export function getCorrelationId(): string {
  return correlationId;
}

const SENSITIVE_KEYS = [
  'password',
  'token',
  'access_token',
  'refresh_token',
  'secret',
  'secret_hash',
  'key_hash',
  'apikey',
  'api_key',
  'authorization',
  'card',
  'pan',
  'cvv',
  'iban',
  'public_token_hash',
];

/** Sostituisce i valori sensibili con un segnaposto, ricorsivamente. */
export function redact(value: unknown, depth = 0): unknown {
  if (depth > 6 || value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map((entry) => redact(entry, depth + 1));
  if (typeof value !== 'object') return value;

  const result: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    result[key] = SENSITIVE_KEYS.some((sensitive) => key.toLowerCase().includes(sensitive))
      ? '[omesso]'
      : redact(entry, depth + 1);
  }
  return result;
}

interface StructuredLog {
  timestamp: string;
  level: LogLevel;
  message: string;
  correlationId: string;
  environment: string;
  context?: unknown;
}

function emit(level: LogLevel, message: string, context?: LogContext): void {
  const payload: StructuredLog = {
    timestamp: new Date().toISOString(),
    level,
    message,
    correlationId,
    environment: env.VITE_APP_ENV,
    ...(context ? { context: redact(context) } : {}),
  };

  // In sviluppo il log strutturato resta leggibile in console; in produzione è
  // il formato che un collettore (OpenTelemetry, Dynatrace) può ingerire.
  if (level === 'error') {
    console.error(JSON.stringify(payload));
  } else if (level === 'warn') {
    console.warn(JSON.stringify(payload));
  } else if (!isProduction) {
    console.warn(JSON.stringify(payload));
  }
}

export const log = {
  debug: (message: string, context?: LogContext) => emit('debug', message, context),
  info: (message: string, context?: LogContext) => emit('info', message, context),
  warn: (message: string, context?: LogContext) => emit('warn', message, context),
};

export function logError(error: unknown, context?: LogContext): void {
  const normalized =
    error instanceof Error
      ? { name: error.name, message: error.message, stack: error.stack }
      : { message: String(error) };

  emit('error', normalized.message, { ...context, error: normalized });
}

/**
 * Misura la durata di un'operazione. I tempi delle azioni critiche (invio in
 * cucina, incasso, chiusura cassa) sono la base per le metriche operative.
 */
export async function measure<T>(name: string, operation: () => Promise<T>): Promise<T> {
  const startedAt = performance.now();
  try {
    const result = await operation();
    log.debug(`${name} completata`, { durationMs: Math.round(performance.now() - startedAt) });
    return result;
  } catch (error) {
    logError(error, { operation: name, durationMs: Math.round(performance.now() - startedAt) });
    throw error;
  }
}
